import type { FirmadokIcon } from '@/components_deprecated'
import type { ActiveModuleName } from '@/modules/companies/CompanySettings/hooks'
import {
  useCompositeModules,
  useModuleConfigs,
} from '@/modules/companies/CompanySettings/hooks'
import { writeAuthCookie } from '@/modules/login/util'
import type { ResultNode } from '@/types/util'
import { FLEET_URL, PLANNER_URL } from '@/utils/env'
import {
  useAdmin,
  useModuleConfig,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO, startOfMonth, subDays, subMonths } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { Module, ModuleOptionsValue } from '../companies/consts'
import { SIDEBAR_QUERY } from './queries'

type ModuleName = ActiveModuleName | 'main'

type FormTemplate = ResultNode<typeof SIDEBAR_QUERY, 'allFormTemplates'>

interface ItemGroup {
  type: 'group'
  header: string
  tall?: boolean

  module?: ModuleName
  permissions?: string | string[]
  hide?: boolean

  items: SidebarItem[]
}

interface SidebarItem {
  type: 'item'
  to: string
  text: string
  end?: true

  className?: string
  icon: FirmadokIcon
  iconWidth?: string
  marginOverride?: string
  badge?: string | number
  badgeLoading?: boolean

  target?: '_blank'

  module?: ModuleName
  permissions?: string | string[]
  hide?: boolean

  onBeforeNavigate?(): void
}

type SidebarConfig = (ItemGroup | SidebarItem)[]

export function useSidebarConfig(): SidebarConfig {
  const translations = useTranslate({
    labelGeneral: 'common.general',
    labelDocumentation: 'common.documentation',
    labelInformation: 'common.information',
    forms: 'common.forms',
    drivingWithoutCard: 'activities.driving-without-card',

    remainingDrivingTime: 'common.remaining-driving-time',

    menu: 'common.menu',

    dashboard: 'common.dashboard',
    users: 'common.users',
    forum: 'common.forum',
    drivingData: 'common.driving-data',
    issues: 'common.issues',
    infringements: 'common.infringements',
    timeEntries: 'common.hourlist',
    absences: 'common.absence',
    handbook: 'common.driversHandbook',
    formCreator: 'common.formcreator',
    companySettings: 'settings.company-settings',
    vehicleFleet: 'vehicles.fleet-overview',
    fleet: 'vehicles.fleet',
    driverPlanner: 'common.driver-planner',
    fleetManagement: 'vehicles.fleet-management',
    routePlanner: 'common.route-planner',
    userExports: 'exports.user-exports',
    salaryBases: 'salary.salary-bases',
    workTimeAndSalary: 'salary.work-time-and-salary',
    checklists: 'common.checklists',
    exportAndSettings: 'common.export-and-settings',
    confirmHours: 'salary.confirm-hours',
    statusActivities: 'activities.activities-status',
    fileArchive: 'activities.file-archive',
    analytics: 'common.analytics',
    startStopCountryRegistrations:
      'activities.start-stop-country-registrations',
  })

  const admin = useAdmin()
  const me = useUser()

  const { active } = useModuleConfigs()
  const activityModule = useModuleConfig(Module.ACTIVITIES)
  const salaryModule = useModuleConfig(Module.SALARY)
  const { mainModuleActive } = useCompositeModules()
  const { hasPermissions } = usePermissions()

  const { data, loading } = useQuery(SIDEBAR_QUERY, {
    variables: {
      workPeriodStartDate: formatISO(startOfMonth(subDays(new Date(), 14))),
      filterEndDate: formatISO(new Date()),
      user: me.id,
      infringementUser: admin ? undefined : me.id,
      lastYear: formatISO(subMonths(new Date(), 12)),
    },
  })

  const issueCount = data?.allIssues.totalUnresolvedCount
  const absenceCount = data?.absenceCountByStatus
  const infringementCount = data?.allInfringements.total
  const confirmSalaryCount = data?.allWorkPeriods.total

  const showAbsenceCounter =
    admin || hasPermissions(PERMISSIONS.timesheets.view.absence)

  const showFileUpload =
    activityModule.getModuleOptionStatus(
      ModuleOptionsValue.ACTIVITIES_READ_TACHO_FILES_TO_ACTIVITIES
    ) &&
    activityModule.getModuleOptionStatus(
      ModuleOptionsValue.ACTIVITIES_PROCESS_TACHO_FILES
    )

  const formTemplates = useMemo(
    () =>
      data?.allFormTemplates.edges.reduce<FormTemplate[]>(
        (acc, edge) => (edge?.node ? [...acc, edge.node] : acc),
        []
      ) ?? [],
    [data?.allFormTemplates.edges]
  )

  const canSeeOthersWorkPeriods = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )

  const canApproveOwnHours = salaryModule.getModuleOptionStatus(
    ModuleOptionsValue.SALARY_USER_APPROVE_HOURS
  )

  const canApproveOthersHours =
    salaryModule.getModuleOptionStatus(
      ModuleOptionsValue.SALARY_LEADER_APPROVE_HOURS
    ) && canSeeOthersWorkPeriods

  const canTransferWorkToAbsence = salaryModule.getModuleOptionStatus(
    ModuleOptionsValue.SALARY_ALLOW_TRANSFER_OF_HOURS_TO_ABSENCES
  )

  const fullConfig = useMemo<SidebarConfig>(
    () => [
      {
        type: 'item',
        to: '/dashboard',
        text: translations.dashboard,
        icon: 'dashboard',
        iconWidth: '18px',
      },
      {
        type: 'group',
        header: translations.labelGeneral,
        tall: isMobile,
        items: [
          {
            type: 'item',
            to: '/users',
            text: translations.users,
            end: true,
            icon: 'users',
            permissions: PERMISSIONS.users.view.user,
          },
          {
            type: 'item',
            to: '/forum',
            text: translations.forum,
            icon: 'chat',
            module: 'forum',
            permissions: PERMISSIONS.forum.view.thread,
          },
        ],
      },
      {
        type: 'group',
        header: translations.workTimeAndSalary,
        module: 'salary',
        items: [
          {
            type: 'item',
            to: '/work-periods',
            text: translations.workTimeAndSalary,
            icon: 'users',
            module: 'salary',
            permissions: PERMISSIONS.salary.view.salarybase,
          },
          {
            type: 'item',
            to: '/approve-work-periods',
            text: translations.confirmHours,
            badge: confirmSalaryCount,
            icon: 'users',
            module: 'salary',
            hide:
              !canApproveOwnHours &&
              !canApproveOthersHours &&
              !canTransferWorkToAbsence,
            permissions: PERMISSIONS.salary.approve.workperiod,
          },
        ],
      },
      {
        type: 'group',
        header: translations.labelDocumentation,
        module: 'main',
        items: [
          {
            type: 'item',
            to: '/activities',
            text: translations.drivingData,
            icon: 'truck',
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
          {
            type: 'item',
            to: '/issues',
            text: translations.issues,
            icon: 'warning',
            badge: admin ? issueCount ?? 0 : undefined,
            badgeLoading: loading,
            module: 'issues',
            permissions: PERMISSIONS.issues.view.issue,
          },
          {
            type: 'item',
            to: '/infringements',
            text: translations.infringements,
            badge: infringementCount,
            icon: 'infringement',
            module: 'infringements',
            permissions: PERMISSIONS.infringements.view.infringement,
          },
          {
            type: 'item',
            to: '/time-entries',
            text: translations.timeEntries,
            className: 'time-entries',
            icon: 'clock',
            iconWidth: '20px',
            module: 'timesheets',
            permissions: PERMISSIONS.timesheets.view.timeentry,
          },
          {
            type: 'item',
            to: '/absences',
            text: translations.absences,
            icon: 'user-x',
            badge: showAbsenceCounter ? absenceCount : undefined,
            badgeLoading: loading,
            module: 'absences',
            permissions: PERMISSIONS.timesheets.view.absence,
          },
        ],
      },
      {
        type: 'group',
        header: translations.fleet,
        module: 'vehicles',
        items: [
          {
            type: 'item',
            to: '/vehicle-fleet',
            text: translations.vehicleFleet,
            className: 'vehicle-fleet',
            icon: 'equipment-materials',
            iconWidth: '26px',
            marginOverride: '1.075rem',
            permissions: PERMISSIONS.vehicles.view.vehicle,
          },
          {
            type: 'item',
            to: FLEET_URL,
            text: translations.fleetManagement,
            className: 'fleet',
            icon: 'truck-side',
            iconWidth: '26px',
            marginOverride: '1.075rem',
            target: '_blank',
            module: 'fleet',
            permissions: PERMISSIONS.fleet.view.vehiclegroup,
            onBeforeNavigate: () => writeAuthCookie(),
          },
          {
            type: 'item',
            to: PLANNER_URL,
            text: translations.driverPlanner,
            icon: 'calendar-week',
            marginOverride: '1.075rem',
            target: '_blank',
            module: 'driverPlanner',
            permissions: PERMISSIONS.planner.view.calendaritem,
            onBeforeNavigate: () => writeAuthCookie(),
          },
          {
            type: 'item',
            to: '/remaining-driving',
            text: translations.remainingDrivingTime,
            icon: 'truck',
            module: 'activities',
            permissions: PERMISSIONS.activities.view.driveractivity,
          },
        ],
      },
      {
        type: 'group',
        header: translations.analytics,
        module: 'activities',
        hide: !showFileUpload,
        permissions: PERMISSIONS.activities.change.driveractivity,
        items: [
          {
            type: 'item',
            to: '/activities/file-archive',
            text: translations.fileArchive + ' (C)',
            icon: 'users',
            module: 'activities',
            hide: !showFileUpload,
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/activities/status',
            text: translations.statusActivities,
            icon: 'users',
            module: 'activities',
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/activities/start-stop-country-registrations',
            text: translations.startStopCountryRegistrations,
            icon: 'users',
            module: 'activities',
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/activities/vehicle-file-archive',
            text: translations.fileArchive + ' (M)',
            icon: 'truck',
            module: 'activities',
            hide: !showFileUpload,
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/vehicles/status',
            text: translations.statusActivities,
            icon: 'truck',
            module: 'activities',
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
          {
            type: 'item',
            to: '/vehicles/driving-without-card',
            text: translations.drivingWithoutCard,
            icon: 'truck',
            module: 'activities',
            permissions: PERMISSIONS.activities.change.driveractivity,
          },
        ],
      },
      {
        type: 'group',
        header: translations.forms,
        permissions: PERMISSIONS.formcreator.view.formtemplate,
        items: formTemplates.map<SidebarItem>(node => ({
          type: 'item',
          to: `/formcreator/${node.id}`,
          text: node.name,
          icon: 'form-creator',
          permissions: PERMISSIONS.formcreator.view.formtemplate,
        })),
      },
      {
        type: 'group',
        header: translations.labelInformation,
        items: [
          {
            type: 'item',
            to: '/handbook',
            text: translations.handbook,
            icon: 'book',
            module: 'handbook',
            permissions: PERMISSIONS.handbook.view.manualfolder,
          },
          {
            type: 'item',
            to: '/formcreator',
            end: true,
            text: translations.formCreator,
            icon: 'form-creator',
            module: 'handbook',
            permissions: PERMISSIONS.formcreator.view.formtemplate,
          },
        ],
      },

      {
        type: 'item',
        to: '/checklists',
        end: true,
        text: translations.checklists,
        icon: 'checkmark',
        module: 'handbook',
        permissions: PERMISSIONS.formcreator.change.filledoutform,
      },
      {
        type: 'group',
        header: translations.routePlanner,
        module: 'routePlanner',
        hide: !admin,
        items: [
          {
            type: 'item',
            to: '/route-planner',
            text: translations.routePlanner,
            icon: 'route-planner',
            iconWidth: '24px',
            permissions: PERMISSIONS.companies.view.companysetting,
          },
        ],
      },
      {
        type: 'group',
        header: translations.exportAndSettings,
        items: [
          {
            type: 'item',
            to: '/user-exports',
            text: translations.userExports,
            className: 'user-exports',
            icon: 'file-arrow-down',
            iconWidth: '24px',
            marginOverride: '1.2rem',
            permissions: PERMISSIONS.companies.export.exportReports,
          },
          {
            type: 'item',
            to: '/salary-bases',
            text: translations.salaryBases,
            icon: 'file-arrow-down',
            iconWidth: '24px',
            marginOverride: '1.2rem',
            module: 'salary',
            permissions: PERMISSIONS.salary.change.salarybase,
          },
          {
            type: 'item',
            to: '/settings',
            text: translations.companySettings,
            className: 'settings',
            icon: 'cog',
            iconWidth: '24px',
            permissions: PERMISSIONS.companies.view.companysetting,
          },
        ],
      },
    ],
    [
      translations.dashboard,
      translations.labelGeneral,
      translations.users,
      translations.forum,
      translations.workTimeAndSalary,
      translations.confirmHours,
      translations.labelDocumentation,
      translations.drivingData,
      translations.issues,
      translations.infringements,
      translations.timeEntries,
      translations.absences,
      translations.fleet,
      translations.vehicleFleet,
      translations.fleetManagement,
      translations.driverPlanner,
      translations.remainingDrivingTime,
      translations.analytics,
      translations.fileArchive,
      translations.statusActivities,
      translations.startStopCountryRegistrations,
      translations.drivingWithoutCard,
      translations.forms,
      translations.labelInformation,
      translations.handbook,
      translations.formCreator,
      translations.checklists,
      translations.routePlanner,
      translations.exportAndSettings,
      translations.userExports,
      translations.salaryBases,
      translations.companySettings,
      confirmSalaryCount,
      canApproveOwnHours,
      canApproveOthersHours,
      canTransferWorkToAbsence,
      admin,
      issueCount,
      loading,
      infringementCount,
      showAbsenceCounter,
      absenceCount,
      showFileUpload,
      formTemplates,
    ]
  )

  const shouldShow = useCallback(
    (item: SidebarConfig[number]): boolean => {
      if ('hide' in item && item.hide) return false

      if (item.module) {
        if (item.module === 'main') return mainModuleActive
        if (!active[item.module]) return false
      }

      if ('permissions' in item) {
        if (!item.permissions) return true
        return hasPermissions(item.permissions)
      }

      return true
    },
    [active, hasPermissions, mainModuleActive]
  )

  const finalConfig = useMemo<SidebarConfig>(() => {
    const config: SidebarConfig = []

    for (const item of fullConfig) {
      if (!shouldShow(item)) continue

      if (item.type === 'item') {
        config.push(item)
      } else {
        const group: ItemGroup = { ...item, items: [] }

        for (const subItem of item.items) {
          if (!shouldShow(subItem)) continue
          group.items.push(subItem)
        }

        if (group.items.length > 0) config.push(group)
      }
    }

    return config
  }, [fullConfig, shouldShow])

  return finalConfig
}
